import { IconEdit, IconPlus, IconTrash } from "@tabler/icons-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useDeleteMenuCollection,
  useMenuCollections,
  useUpdateMenuCollection,
} from "@/hooks/use-menu";
import { useActiveOrganization } from "@/hooks/use-organizations";

const formatDate = (value: string) => {
  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(value));
};

export default function MenuCollectionsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { data: activeOrganization, isLoading: isLoadingOrganization } = useActiveOrganization();
  const { data, error, isLoading } = useMenuCollections(activeOrganization?.id);
  const updateCollection = useUpdateMenuCollection();
  const deleteCollection = useDeleteMenuCollection();

  const handleDelete = (collectionId: string) => {
    deleteCollection.mutate(collectionId, {
      onError: (mutationError) => {
        toast.error(mutationError.message);
      },
      onSuccess: () => {
        toast.success(t("menu.collections.notifications.deleted"));
      },
    });
  };

  const handleActiveChange = (collectionId: string, active: boolean) => {
    updateCollection.mutate(
      {
        data: { active },
        id: collectionId,
      },
      {
        onError: (mutationError) => {
          toast.error(mutationError.message);
        },
      },
    );
  };

  if (isLoadingOrganization) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!activeOrganization) {
    return (
      <div className="p-6">
        <Card>
          <CardHeader>
            <CardTitle>{t("menu.collections.title")}</CardTitle>
            <CardDescription>
              {t("menu.collections.notifications.activeOrgRequired")}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  let collectionsContent: ReactNode = (
    <div className="rounded-lg border border-dashed p-8 text-center">
      <h2 className="font-medium text-base">{t("menu.collections.table.emptyTitle")}</h2>
      <p className="mt-2 text-muted-foreground text-sm">
        {t("menu.collections.table.emptyDescription")}
      </p>
    </div>
  );

  if (isLoading) {
    collectionsContent = (
      <div className="space-y-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    );
  } else if (error) {
    collectionsContent = <p className="text-destructive text-sm">{error.message}</p>;
  } else if (data?.collections.length) {
    collectionsContent = (
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>{t("menu.collections.table.name")}</TableHead>
            <TableHead>{t("menu.collections.table.slug")}</TableHead>
            <TableHead>{t("menu.collections.table.active")}</TableHead>
            <TableHead>{t("menu.collections.table.updated")}</TableHead>
            <TableHead className="text-right">{t("menu.collections.table.actions")}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {data.collections.map((collection) => (
            <TableRow key={collection.id}>
              <TableCell>
                <div className="space-y-1">
                  <p className="font-medium">{collection.name}</p>
                  {collection.description ? (
                    <p className="text-muted-foreground text-xs">{collection.description}</p>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">/{collection.slug}</TableCell>
              <TableCell>
                <Switch
                  checked={collection.active}
                  onCheckedChange={(checked) => handleActiveChange(collection.id, checked)}
                />
              </TableCell>
              <TableCell className="text-muted-foreground text-sm">
                {formatDate(collection.updatedAt)}
              </TableCell>
              <TableCell>
                <div className="flex justify-end gap-2">
                  <Button
                    onClick={() => navigate(`/menu/collections/${collection.id}/edit`)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconEdit className="size-4" />
                    <span className="sr-only">{t("menu.collections.actions.edit")}</span>
                  </Button>
                  <Button
                    onClick={() => handleDelete(collection.id)}
                    size="icon"
                    type="button"
                    variant="outline"
                  >
                    <IconTrash className="size-4" />
                    <span className="sr-only">{t("menu.collections.actions.delete")}</span>
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    );
  }

  return (
    <div className="flex flex-col gap-8 p-6">
      <div className="flex items-center justify-between">
        <div className="space-y-1">
          <h1 className="font-serif font-semibold text-2xl tracking-tight">
            {t("menu.collections.title")}
          </h1>
          <p className="text-muted-foreground text-sm">
            {t("menu.collections.description", {
              organization: activeOrganization.name,
            })}
          </p>
        </div>
        <Button onClick={() => navigate("/menu/collections/new")} type="button">
          <IconPlus className="mr-2 size-4" />
          {t("menu.collections.newButton")}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-serif">{t("menu.collections.table.title")}</CardTitle>
          <CardDescription>{t("menu.collections.table.description")}</CardDescription>
        </CardHeader>
        <CardContent>{collectionsContent}</CardContent>
      </Card>
    </div>
  );
}
